// src/modules/public/components/HelpView/index.js
import React from 'react';
import { Accordion, Card, ListGroup, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import './styles.css';

const StravaIcon = ({ width = 18, height = 18 }) => (
    <svg viewBox="0 0 24 24" width={width} height={height} fill="#fc4c02" style={{ flexShrink: 0 }}>
        <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169" />
    </svg>
);

const HelpView = () => {
    return (
        <div className="help-container">
            <Card className="mb-4 help-intro-card">
                <Card.Body>
                    <h3 className="mb-2">How FitOps Works</h3>
                    <p className="mb-0">
                        FitOps is a team fitness competition platform. Log activities manually or
                        connect Strava for automatic syncing, earn points, and compete with your
                        teammates. Use the sections below to get up to speed.
                    </p>
                </Card.Body>
            </Card>

            <Accordion defaultActiveKey="0" alwaysOpen className="help-accordion">

                {/* ── Getting Started ── */}
                <Accordion.Item eventKey="0">
                    <Accordion.Header>
                        <span className="help-section-icon">🚀</span>
                        Getting Started
                    </Accordion.Header>
                    <Accordion.Body>
                        <ListGroup variant="flush">
                            <ListGroup.Item>
                                <strong>Create an account</strong> — sign up with your name and
                                email. Your username is set during signup and cannot be changed.
                            </ListGroup.Item>
                            <ListGroup.Item>
                                <strong>Log in and go to FitHub</strong> — your personal dashboard
                                showing your activity feed, active competitions, and milestones.
                            </ListGroup.Item>
                            <ListGroup.Item>
                                <strong>Set up your profile</strong> — add a profile picture and
                                review your all-time stats from the profile page. Reach it via the
                                avatar dropdown in the top-right corner.
                            </ListGroup.Item>
                            <ListGroup.Item>
                                <strong>Join a competition</strong> — open <em>Competitions</em> from
                                the dropdown, find an active or upcoming competition, and click Join.
                            </ListGroup.Item>
                        </ListGroup>
                    </Accordion.Body>
                </Accordion.Item>

                {/* ── Logging Activities ── */}
                <Accordion.Item eventKey="1">
                    <Accordion.Header>
                        <span className="help-section-icon">📝</span>
                        Logging Activities
                    </Accordion.Header>
                    <Accordion.Body>
                        <p>
                            Open <strong>Register Activity</strong> from the dropdown menu to log a
                            workout manually.
                        </p>
                        <h6>Supported activity types</h6>
                        <ListGroup className="mb-3">
                            <ListGroup.Item className="d-flex justify-content-between align-items-center">
                                Running
                                <Badge bg="success">Min 2 km · pace ≤ 9 min/km</Badge>
                            </ListGroup.Item>
                            <ListGroup.Item className="d-flex justify-content-between align-items-center">
                                Cycling
                                <Badge bg="primary">Min 10 km · speed ≥ 18 km/h</Badge>
                            </ListGroup.Item>
                            <ListGroup.Item className="d-flex justify-content-between align-items-center">
                                Swimming
                                <Badge bg="info" text="dark">Min 100 m</Badge>
                            </ListGroup.Item>
                            <ListGroup.Item className="d-flex justify-content-between align-items-center">
                                Hiking
                                <Badge bg="warning" text="dark">Min 1.5 km · pace ≤ 20 min/km</Badge>
                            </ListGroup.Item>
                        </ListGroup>
                        <p>
                            Activities that don't meet the minimum requirements score 0 points and
                            are not saved. See the <Link to="/rules">Rules page</Link> for the full
                            points formula.
                        </p>
                        <p className="mb-0">
                            Activities must be logged while a competition is active. Anything
                            submitted after the competition's end date will not count toward
                            that competition's score.
                        </p>
                    </Accordion.Body>
                </Accordion.Item>

                {/* ── Strava Integration ── */}
                <Accordion.Item eventKey="2">
                    <Accordion.Header>
                        <StravaIcon />
                        Strava Integration
                    </Accordion.Header>
                    <Accordion.Body>
                        <p>
                            Connect your Strava account once and all new activities sync
                            automatically — no manual entry needed.
                        </p>
                        <h6>How to connect</h6>
                        <ListGroup className="mb-3">
                            <ListGroup.Item>
                                Go to <strong>My Profile</strong> → click <em>Connect Strava</em>.
                            </ListGroup.Item>
                            <ListGroup.Item>
                                Authorise FitOps in the Strava consent screen.
                            </ListGroup.Item>
                            <ListGroup.Item>
                                You'll be redirected back and your account is linked. A Strava badge
                                will appear on all synced activities.
                            </ListGroup.Item>
                        </ListGroup>
                        <h6>What syncs automatically</h6>
                        <ListGroup className="mb-3">
                            <ListGroup.Item>
                                <strong>New activity</strong> — synced immediately when you save it
                                on Strava, including a route map generated from your GPS track.
                            </ListGroup.Item>
                            <ListGroup.Item>
                                <strong>Edited activity</strong> — if you crop, rename, or retype an
                                activity on Strava, FitOps updates the record and refreshes the
                                route image automatically.
                            </ListGroup.Item>
                            <ListGroup.Item>
                                <strong>Deleted activity</strong> — removed from FitOps and points
                                are reversed.
                            </ListGroup.Item>
                            <ListGroup.Item>
                                <strong>Made private on Strava</strong> — treated as a delete;
                                the activity is removed from FitOps. If you later make it public
                                again, Strava re-delivers it as a new create and it will be synced
                                again.
                            </ListGroup.Item>
                        </ListGroup>
                        <h6>Supported Strava activity types</h6>
                        <p className="mb-0">
                            Run, Ride, Swim, Hike, Walk, TrailRun, VirtualRun, VirtualRide.
                            Other types (e.g. Yoga, WeightTraining) are ignored.
                        </p>
                    </Accordion.Body>
                </Accordion.Item>

                {/* ── Points & Scoring ── */}
                <Accordion.Item eventKey="3">
                    <Accordion.Header>
                        <span className="help-section-icon">⭐</span>
                        Points &amp; Scoring
                    </Accordion.Header>
                    <Accordion.Body>
                        <ListGroup className="mb-3">
                            <ListGroup.Item>
                                <strong>Running:</strong> 1 pt / km + 1 pt / 100 m elevation
                            </ListGroup.Item>
                            <ListGroup.Item>
                                <strong>Cycling:</strong> 0.25 pts / km + 1 pt / 200 m elevation
                            </ListGroup.Item>
                            <ListGroup.Item>
                                <strong>Swimming:</strong> 4 pts / km
                            </ListGroup.Item>
                            <ListGroup.Item>
                                <strong>Hiking:</strong> 0.5 pts / km + 0.5 pts / 100 m elevation
                            </ListGroup.Item>
                        </ListGroup>
                        <p>
                            Points are calculated at the time of saving and are not retroactively
                            adjusted if the rules change. Only activities within a competition's
                            start and end dates count toward that competition's leaderboard.
                        </p>
                        <p className="mb-0">
                            Full minimum requirements and edge cases are on the{' '}
                            <Link to="/rules">Rules page</Link>.
                        </p>
                    </Accordion.Body>
                </Accordion.Item>

                {/* ── Late-Logging Penalty ── */}
                <Accordion.Item eventKey="3b">
                    <Accordion.Header>
                        <span className="help-section-icon">⏰</span>
                        Late-Logging Penalty
                    </Accordion.Header>
                    <Accordion.Body>
                        <p>
                            To keep scoring fair, activities logged more than 24 hours after
                            they took place receive a points reduction.
                        </p>
                        <h6>How the penalty is calculated</h6>
                        <ListGroup className="mb-3">
                            <ListGroup.Item>
                                <strong>On time (≤ 24 h):</strong> no penalty — full points.
                            </ListGroup.Item>
                            <ListGroup.Item>
                                <strong>1–2 days late:</strong> 20% reduction (×0.8).
                            </ListGroup.Item>
                            <ListGroup.Item>
                                <strong>2–3 days late:</strong> 40% reduction (×0.6).
                            </ListGroup.Item>
                            <ListGroup.Item>
                                <strong>3–4 days late:</strong> 60% reduction (×0.4).
                            </ListGroup.Item>
                            <ListGroup.Item>
                                <strong>4–5 days late:</strong> 80% reduction (×0.2).
                            </ListGroup.Item>
                            <ListGroup.Item>
                                <strong>5+ days late:</strong> 100% reduction — 0 points.
                            </ListGroup.Item>
                        </ListGroup>
                        <p>
                            When a penalty applies, the activity detail page shows both the
                            original (raw) points and the effective points after the reduction.
                        </p>
                        <h6>Who is exempt</h6>
                        <ListGroup className="mb-3">
                            <ListGroup.Item>
                                <strong>New users</strong> — a 3-day grace period applies from
                                the date you created your account, so your first few activities
                                are never penalised.
                            </ListGroup.Item>
                            <ListGroup.Item>
                                <strong>Old activities</strong> — anything registered before the
                                penalty feature was launched is permanently exempt.
                            </ListGroup.Item>
                        </ListGroup>
                        <h6>Requesting an exemption</h6>
                        <p>
                            If you logged an activity late for a legitimate reason (illness,
                            travel, technical issue), you can request an exemption directly
                            from the activity detail page. Add an optional note explaining the
                            situation and submit the request.
                        </p>
                        <ListGroup className="mb-3">
                            <ListGroup.Item>
                                Admins review pending exemption requests and will approve
                                or reject them.
                            </ListGroup.Item>
                            <ListGroup.Item>
                                An <strong>approved</strong> exemption fully restores the
                                original points.
                            </ListGroup.Item>
                            <ListGroup.Item>
                                A <strong>rejected</strong> exemption is final — you cannot
                                submit a second request for the same activity.
                            </ListGroup.Item>
                        </ListGroup>
                    </Accordion.Body>
                </Accordion.Item>

                {/* ── Competitions ── */}
                <Accordion.Item eventKey="4">
                    <Accordion.Header>
                        <span className="help-section-icon">🏆</span>
                        Competitions
                    </Accordion.Header>
                    <Accordion.Body>
                        <ListGroup className="mb-3">
                            <ListGroup.Item>
                                <strong>Upcoming</strong> — competition hasn't started yet. You can
                                join to be ready on day one.
                            </ListGroup.Item>
                            <ListGroup.Item>
                                <strong>Active</strong> — in progress. Every activity you log
                                (manual or Strava) that falls within the dates adds to your score.
                            </ListGroup.Item>
                            <ListGroup.Item>
                                <strong>Concluded</strong> — finished. The winner is shown, and
                                the final leaderboard and statistics are available for review.
                            </ListGroup.Item>
                        </ListGroup>
                        <p className="mb-0">
                            Open the <strong>Statistics</strong> view inside any competition to
                            see cumulative points over time, daily breakdowns, and projected
                            end-of-competition scores.
                        </p>
                    </Accordion.Body>
                </Accordion.Item>

                {/* ── FAQ ── */}
                <Accordion.Item eventKey="6">
                    <Accordion.Header>
                        <span className="help-section-icon">❓</span>
                        Frequently Asked Questions
                    </Accordion.Header>
                    <Accordion.Body>
                        <div className="help-faq-item">
                            <strong>Can I log the same activity twice?</strong>
                            <p>
                                Be careful — duplicate manual entries will both count toward your
                                score. If Strava is connected, do not also log the activity
                                manually or it will be counted twice.
                            </p>
                            <div className="admonition admonition-warning">
                                <span className="admonition-icon">⚠️</span>
                                <div className="admonition-body">
                                    <strong>Warning</strong>
                                    Cheating carries a penalty: you owe cake to every
                                    user on FitOps, regardless of whether they are in a competition.
                                </div>
                            </div>
                        </div>
                        <div className="help-faq-item">
                            <strong>My Strava activity didn't sync. What happened?</strong>
                            <p>
                                Check that the activity type is supported and that it meets the
                                minimum distance and pace requirements.
                            </p>
                            <p className="mb-0">
                                If the activity is private, whether it syncs depends on the
                                permissions you granted when connecting Strava. If you allowed
                                access to private activities, they will sync. If you didn't, private
                                activities are invisible to FitOps. This cannot be changed without
                                first revoking FitOps access in your Strava settings, then
                                reconnecting from your FitOps profile and granting private activity
                                access this time.
                            </p>
                        </div>
                        <div className="help-faq-item">
                            <strong>I edited an activity on Strava. Will FitOps update?</strong>
                            <p>
                                Yes. Strava sends FitOps an update event and the record — including
                                the route map — is refreshed automatically within a few seconds.
                            </p>
                        </div>
                        <div className="help-faq-item">
                            <strong>Does disconnecting Strava delete my synced activities?</strong>
                            <p className="mb-0">
                                No. Disconnecting only stops future syncing; previously synced
                                activities remain in FitOps.
                            </p>
                        </div>
                    </Accordion.Body>
                </Accordion.Item>

            </Accordion>
        </div>
    );
};

export default HelpView;
